import React, { useState } from 'react';
import { Mail, Lock, AlertCircle, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Input } from '../ui';
import GoogleSignInButton from './GoogleSignInButton';

interface LoginFormProps {
  onToggleMode: () => void;
  onForgotPassword: () => void;
  onBack?: () => void;
}

export const LoginForm: React.FC<LoginFormProps> = ({ onToggleMode, onForgotPassword, onBack }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmitting) return;
    setError('');

    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    try {
      await login(email, password);
    } catch (err: unknown) {
      const error = err as { message?: string; error_description?: string };
      setError(error?.message || error?.error_description || 'Invalid login credentials');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      {onBack && (
        <Button
          onClick={onBack}
          variant="link"
          leftIcon={<ArrowLeft className="w-4 h-4 transition-transform duration-200 group-hover:-translate-x-0.5" />}
          className="text-[13px] mb-6"
        >
          Back
        </Button>
      )}

      <div className="text-center mb-6 sm:mb-8">
        <h2 className="text-[22px] sm:text-[24px] font-semibold text-primary-900 mb-1.5 tracking-tight">
          Welcome back
        </h2>
        <p className="text-[13px] sm:text-sm text-neutral-500">
          Sign in to continue to Skylarq
        </p>
      </div>

      <div className="mb-6">
        <GoogleSignInButton />
      </div>

      <div className="flex items-center gap-4 mb-6">
        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-neutral-200 to-transparent" />
        <span className="text-xs text-neutral-400 font-medium">or continue with email</span>
        <div className="flex-1 h-px bg-gradient-to-r from-transparent via-neutral-200 to-transparent" />
      </div>

      <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
        <Input
          id="email"
          type="email"
          label="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isSubmitting}
          placeholder="you@company.com"
          autoComplete="email"
          autoFocus
          fullWidth
          inputSize="lg"
          leftIcon={<Mail className="w-4 h-4" />}
        />

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label htmlFor="password" className="text-[13px] font-medium text-neutral-700">
              Password
            </label>
            <button
              id="forgot-password-btn"
              type="button"
              onClick={onForgotPassword}
              className="text-[11px] sm:text-xs font-medium text-neutral-400 hover:text-primary-900 transition-colors duration-200"
            >
              Forgot password?
            </button>
          </div>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isSubmitting}
            placeholder="Enter your password"
            autoComplete="current-password"
            fullWidth
            inputSize="lg"
            leftIcon={<Lock className="w-4 h-4" />}
            showPasswordToggle
          />
        </div>

        {error && (
          <div className="flex items-center gap-2.5 p-3.5 bg-red-50 border border-red-100 rounded-xl">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <p className="text-[13px] text-red-600">{error}</p>
          </div>
        )}

        <Button
          id="login-submit-btn"
          type="submit"
          loading={isSubmitting}
          fullWidth
          size="lg"
          className="mt-2"
        >
          {isSubmitting ? 'Signing in...' : 'Sign in'}
        </Button>
      </form>

      <div className="text-center mt-6">
        <p className="text-[13px] text-neutral-500">
          Don't have an account?{' '}
          <button
            id="sign-up-btn"
            onClick={onToggleMode}
            className="font-semibold text-primary-900 hover:text-primary-700 transition-colors duration-200"
          >
            Sign up
          </button>
        </p>
      </div>
    </div>
  );
};
